import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { WhatsAppSettings } from '../../../types/models/whatsapp';

interface SettingsPanelProps {
  open: boolean;
  settings: WhatsAppSettings;
  onClose: () => void;
  onUpdate: (updates: Partial<WhatsAppSettings>) => Promise<void>;
}

interface ToggleDef {
  key: keyof WhatsAppSettings;
  label: string;
  description: string;
  /** Shown when the toggle is in its non-default, riskier position. */
  warning?: string;
}

const TOGGLES: ToggleDef[] = [
  {
    key: 'sendReadReceipts',
    label: 'Send read receipts',
    description:
      'Show blue ticks to contacts when an agent opens their conversation.'
  },
  {
    key: 'autoAssignEnabled',
    label: 'Automatic assignment',
    description:
      'Evenly distribute new chats among online team members (round-robin).'
  },
  {
    key: 'pauseMarketingTemplates',
    label: 'Pause marketing templates',
    description:
      'Blocks all MARKETING-category sends. Utility and service messages continue.',
    warning: 'Marketing sends are currently paused — broadcasts will not go out.'
  }
];

const Toggle: React.FC<{
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
  label: string;
}> = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
      checked ? 'bg-primary-600' : 'bg-gray-300'
    }`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
        checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

/**
 * Account-wide toggles, mirroring the equivalents in Heltar's Settings:
 * Toggle Read Receipts, Auto Assign Chat, Pause Marketing Templates.
 */
const SettingsPanel: React.FC<SettingsPanelProps> = ({
  open,
  settings,
  onClose,
  onUpdate
}) => {
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleToggle = async (key: keyof WhatsAppSettings, value: boolean) => {
    setSavingKey(key);
    setError(null);
    try {
      await onUpdate({ [key]: value });
    } catch (err: any) {
      setError(err.message || 'Failed to save setting');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            WhatsApp settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="divide-y divide-gray-100">
          {TOGGLES.map((toggle) => {
            const checked = settings[toggle.key];
            return (
              <div key={toggle.key} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {toggle.label}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {toggle.description}
                    </p>
                  </div>
                  <Toggle
                    checked={checked}
                    disabled={savingKey !== null}
                    onChange={(value) => handleToggle(toggle.key, value)}
                    label={toggle.label}
                  />
                </div>

                {toggle.warning && checked && (
                  <p className="mt-2 flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-warning-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    {toggle.warning}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <p className="border-t border-gray-100 px-4 py-2 text-xs text-danger-600">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default SettingsPanel;
